import * as Yup from 'yup';
import { FC, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import {
  Container,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Button,
  Typography,
  Autocomplete
} from '@mui/material';
import TextField from '@mui/material/TextField';
import request from '../../../../utils/request';
import { LoadingButton } from '@mui/lab';
import SnackbarAlert from '../../../common/SnackbarAlert';
import React from 'react';
import AutocompleteLoader from '../../../common/AutocompleteLoader';

type CreateEditProps = {
  closeCreateEditForm: any;
  ageEditDatas: any;
  editStatus: boolean;
  productId: any;
  setNewValue?: Function;
};
const CreateEdit: FC<CreateEditProps> = ({
  closeCreateEditForm,
  ageEditDatas,
  editStatus,
  productId,
  setNewValue
}) => {
  const AgeSchema = Yup.object().shape({
    ageName: Yup.string()
      .required('Age Range Name is required')
      .min(3, 'Age Range Name must be at least 3 characters')
      .test(
        'no-leading-space',
        'Cannot start with a space',
        (value) => !value || value.trimStart() === value
      )
      .matches(
        // eslint-disable-next-line no-useless-escape
        /^[a-zA-Z0-9\s\[\]\-\(\)&\/\\<>]*$/,
        'Age Range Name only accepts special characters like &,[,],-,/,(,),<,>'
      ),
    // .matches(
    //   /[a-zA-Z]/,
    //   'Age Name must include at least one letter of the alphabet'
    // ),
    // .test(
    //   'no-leading-space',
    //   'Cannot start with a space',
    //   (value) => !value || value.trimStart() === value
    // ),
    // .test(
    //   'notOnlyWhiteSpace',
    //   'Age Name cannot include leading and trailing spaces',
    //   (value) => {
    //     return !/\s{2,}/.test(value);
    //   }
    // ),
    ageCode: Yup.string()
      .required('Age Code is required')
      .test(
        'notOnlyWhiteSpace',
        'Age Code cannot include leading and trailing spaces',
        (value) => {
          return !/\s{2,}/.test(value);
        }
      )
      .min(2, 'Age Code must be at least 2 characters'),
      // .matches(
      //   // eslint-disable-next-line no-useless-escape
      //   /^[a-zA-Z0-9\s\[\]\-\(\)&\/\\]*$/,
      //   'Age Code only accepts special characters like &,[,],-,/,(,)'
      // ),
    // .matches(
    //   alphanumericSpecialcharacterCodeRegex,
    //   'Age Code only accept special character `-` followed by alphanumeric values and cannot include leading and trailing spaces '
    // )
    // .matches(
    //   /^[^-!@#$%^&*()_+={};:'",.<>/?\\|[\]`~].*$/,
    //   'Age Code cannot start with a special character'
    // )
    ageRate: Yup.number()
      .notRequired()
      .typeError('Age Rate is not valid')
      .positive('Must be a positive number.')
      .min(1, 'Age Rate must be greater than or equal to 1')
      .transform((value, originalValue) => {
        return /\s/.test(originalValue) ? NaN : value;
      })
      .test('len', 'Length is too long', (val) => {
        if (val === undefined || val === null) return true;
        const valStr = val.toString();
        const maxLength = valStr.includes('.') ? 10 : 8;
        return valStr.length <= maxLength;
      })
      .test(
        'max-two-decimals',
        'Age Rate must have at most two decimal places',
        (value) => {
          if (value === undefined || value === null) return true;
          const decimalPlaces = value.toString().split('.')[1];
          return !decimalPlaces || decimalPlaces.length <= 2;
        }
      )
      .nullable(),

    startAge: Yup.number()
      .required('Starting Age is required')
      .typeError('Starting Age is not valid')
      .positive('Must be a positive number.')
      .max(100, 'Starting Age cannot be more than 100')
      .min(0, 'Starting Age must be greater than or equal to 0')
      .transform((value, originalValue) => {
        return /\s/.test(originalValue) ? NaN : value;
      })
      .test('len', 'Length is too long', (val) => {
        if (val === undefined || val === null) return true;
        const valStr = val.toString();
        const maxLength = valStr.includes('.') ? 10 : 8;
        return valStr.length <= maxLength;
      })
      .test('is-integer', 'Decimal numbers are not allowed', (value) => {
        return Number.isInteger(value);
      })
      .nullable(),

    endAge: Yup.number()
      .required('Ending Age is required')
      .typeError('Ending Age is not valid')
      .positive('Must be a positive number.')
      // .max(100, 'Ending Age cannot be more than 100')
      .min(1, 'Ending Age must be greater than or equal to 1')
      .transform((value, originalValue) => {
        return /\s/.test(originalValue) ? NaN : value;
      })
      .test('len', 'Length is too long', (val) => {
        if (val === undefined || val === null) return true;
        const valStr = val.toString();
        const maxLength = valStr.includes('.') ? 10 : 8;
        return valStr.length <= maxLength;
      })
      .test('is-integer', 'Decimal numbers are not allowed', (value) => {
        return Number.isInteger(value);
      })
      .test(
        'is-greater',
        'Ending Age must be greater than or equal to Starting Age',
        function (value) {
          const { startAge } = this.parent;
          return value >= startAge;
        }
      )
      .nullable(),
    productId: Yup.number().required('Product is required')
  });
  const [isLoading, setLoading] = useState<boolean>(true);
  const [loader, setLoader] = useState<boolean>(false);
  const [showSnackBarAlert, setShowSnackBarAlert] = useState<{
    status: boolean;
    type: string;
    message: any;
  }>({
    status: false,
    type: 'success',
    message: null
  });
  const [productData, setProductData] = useState<any>([]);
  const [productSearchValue, setProductSearchValue] = useState<string>('');
  const [productValue, setProductValue] = useState<any>(null);
  const formik = useFormik({
    initialValues: {
      id: '',
      ageName: '',
      ageCode: '',
      ageRate: '',
      startAge: '',
      endAge: '',
      status: true,
      productId: productId
    },
    validationSchema: AgeSchema,
    onSubmit: (values) => {
      setLoader(true);
      const method = editStatus ? 'PUT' : 'POST';
      request({
        url: 'age',
        method: method,
        data: values
      })
        .then((age) => {
          if (age.status) {
            closeCreateEditForm(true);
            setLoader(false);
            if (setNewValue) {
              setNewValue(values?.ageName);
            }
          } else {
            setLoader(false);
            setShowSnackBarAlert({
              status: true,
              type: 'error',
              message: age['message']
            });
          }
        })
        .catch(function (err) {
          setShowSnackBarAlert({
            status: true,
            type: 'error',
            message: err.message
          });
          setLoader(false);
        });
    }
  });

  useEffect(() => {
    if (formik.values.startAge && formik.values.endAge) {
      const ageName = `${formik.values.startAge}-${formik.values.endAge}`;
      formik.setFieldValue('ageName', ageName);
    }
  }, [formik.values.startAge, formik.values.endAge]);
  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSearchValue]);
  const fetchProducts = async () => {
    request({
      url: 'policyProduct/getPolicyProductList',
      method: 'post',
      data: {
        search: productSearchValue,
        orderColumn: 'id',
        orderDirection: 'DESC',
        offset: 0,
        limit: 100,
        status: true
      }
    }).then(
      (response) => {
        setProductData(response['policyProductData']);
        setLoading(false);
      },
      (error) => {
        console.log(error);
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    if (editStatus) {
      fetchPolicyProductNameById();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fetchPolicyProductNameById = async () => {
    request({
      url: 'policyProduct/getPolicyProductNameById',
      method: 'post',
      data: {
        id: ageEditDatas?.productId
      }
    }).then(
      (response: any) => {
        formik.setValues((prev) => ({
          ...prev,
          productId: response?.id
        }));
        setProductSearchValue(response?.name);
        setLoading(false);
      },
      (error) => {
        console.log(error);
        setLoading(false);
      }
    );
  };
  const capitalizeFirst = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  if (editStatus) {
    formik.values.id = ageEditDatas.id;
    formik.initialValues.ageName = ageEditDatas.ageName;
    formik.initialValues.ageCode = ageEditDatas.ageCode;
    formik.initialValues.ageRate = ageEditDatas.ageRate;
    formik.initialValues.startAge = ageEditDatas.startAge;
    formik.initialValues.endAge = ageEditDatas.endAge;
  }
  const handleSnackbarClose = () => {
    setShowSnackBarAlert({ status: false, type: null, message: null });
  };

  return (
    <>
      <SnackbarAlert
        showSnackBarAlert={showSnackBarAlert}
        handleSnackbarClose={handleSnackbarClose}
      />
      <Container maxWidth="lg">
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="stretch"
          spacing={3}
        >
          <Grid item xs={12}>
            <Card>
              {editStatus ? (
                <CardHeader title="Update Age Range " />
              ) : (
                <CardHeader title="Create Age Range " />
              )}

              <CardContent>
                <form onSubmit={formik.handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        label={
                          <React.Fragment>
                            Starting Age{' '}
                            <Typography component="span" sx={{ color: 'red' }}>
                              *
                            </Typography>
                          </React.Fragment>
                        }
                        sx={{ width: '100%' }}
                        size="small"
                        name="startAge"
                        type="number"
                        value={formik.values.startAge}
                        onChange={formik.handleChange}
                        onBlur={(e) => {
                          const formattedValue = e.target.value?.includes('.')
                            ? e.target.value.replace(
                                /(\.\d*?[1-9])0+$|\.0*$/,
                                '$1'
                              )
                            : e.target.value;
                          formik.setFieldValue(
                            'startAge',
                            formattedValue,
                            true
                          );
                          formik.handleBlur(e);
                        }}
                        error={
                          formik.touched.startAge &&
                          Boolean(formik.errors.startAge)
                        }
                        helperText={
                          formik.touched.startAge && formik.errors.startAge
                        }
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        label={
                          <React.Fragment>
                            Ending Age{' '}
                            <Typography component="span" sx={{ color: 'red' }}>
                              *
                            </Typography>
                          </React.Fragment>
                        }
                        sx={{ width: '100%' }}
                        size="small"
                        name="endAge"
                        type="number"
                        value={formik.values.endAge}
                        onChange={formik.handleChange}
                        onBlur={(e) => {
                          const formattedValue = e.target.value?.includes('.')
                            ? e.target.value.replace(
                                /(\.\d*?[1-9])0+$|\.0*$/,
                                '$1'
                              )
                            : e.target.value;
                          formik.setFieldValue('endAge', formattedValue, true);
                          formik.handleBlur(e);
                        }}
                        error={
                          formik.touched.endAge && Boolean(formik.errors.endAge)
                        }
                        helperText={
                          formik.touched.endAge && formik.errors.endAge
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label={
                          <React.Fragment>
                            Age Range Name{' '}
                            <Typography component="span" sx={{ color: 'red' }}>
                              *
                            </Typography>
                          </React.Fragment>
                        }
                        disabled
                        sx={{ width: '100%' }}
                        size="small"
                        name="ageName"
                        value={capitalizeFirst(formik.values.ageName)}
                        onChange={formik.handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          formik.setFieldValue('ageName', trimmedValue);
                          formik.handleBlur(e);
                        }}
                        error={
                          formik.touched.ageName &&
                          Boolean(formik.errors.ageName)
                        }
                        helperText={
                          formik.touched.ageName && formik.errors.ageName
                        }
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        label={
                          <React.Fragment>
                            Age Code{' '}
                            <Typography component="span" sx={{ color: 'red' }}>
                              *
                            </Typography>
                          </React.Fragment>
                        }
                        sx={{ width: '100%' }}
                        size="small"
                        name="ageCode"
                        value={formik.values.ageCode}
                        onChange={formik.handleChange}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          formik.setFieldValue('ageCode', trimmedValue);
                          formik.handleBlur(e);
                        }}
                        error={
                          formik.touched.ageCode &&
                          Boolean(formik.errors.ageCode)
                        }
                        helperText={
                          formik.touched.ageCode && formik.errors.ageCode
                        }
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        label={<React.Fragment>Age Rate </React.Fragment>}
                        sx={{ width: '100%' }}
                        size="small"
                        name="ageRate"
                        type="number"
                        onKeyDown={(e) => {
                          if (['e', 'E', '-', '+'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        value={formik.values.ageRate}
                        onChange={formik.handleChange}
                        onBlur={(e) => {
                          const formattedValue = e.target.value?.includes('.')
                            ? e.target.value.replace(
                                /(\.\d*?[1-9])0+$|\.0*$/,
                                '$1'
                              )
                            : e.target.value;
                          formik.setFieldValue('ageRate', formattedValue, true);
                          formik.handleBlur(e);
                        }}
                        error={
                          formik.touched.ageRate &&
                          Boolean(formik.errors.ageRate)
                        }
                        helperText={
                          formik.touched.ageRate && formik.errors.ageRate
                        }
                      />
                    </Grid>

                    <Grid
                      container
                      direction="row"
                      justifyContent="center"
                      alignItems="center"
                      spacing={3}
                      mt={2}
                    >
                      {loader ? (
                        <LoadingButton
                          sx={{ mt: { xs: 2, md: 0 } }}
                          loading={loader}
                          variant="contained"
                          type="submit"
                        >
                          Save
                        </LoadingButton>
                      ) : (
                        <Button
                          sx={{ mt: { xs: 2, md: 0 } }}
                          type="submit"
                          variant="contained"
                        >
                          Save
                        </Button>
                      )}
                      &nbsp;
                      <Button
                        sx={{ mt: { xs: 2, md: 0 } }}
                        variant="contained"
                        color="error"
                        onClick={closeCreateEditForm}
                      >
                        Cancel
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default CreateEdit;
